import Component from '@glimmer/component';
import { tracked, cached } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { hash } from '@ember/helper';
import type Owner from '@ember/owner';

// ---------------------------------------------------------------------------
// Form – reimagined form orchestrator (validation-library agnostic)
// ---------------------------------------------------------------------------

/**
 * Flattened validation errors keyed by field name.
 * Compatible with Valibot's `v.flatten()`, Zod, or hand-rolled validation.
 */
export type FormErrors = { [key: string]: readonly string[] | undefined };

/**
 * Per-field touched state.
 */
export type Touched = { [key: string]: boolean };

/**
 * A validation function the consumer provides.
 * Given the current data, return a FormErrors map.
 * Return `{}` (empty object) when valid.
 *
 * @example Valibot bridge
 * ```ts
 * const validate = (data) => {
 *   const result = v.safeParse(schema, data);
 *   return result.success ? {} : (v.flatten(result.issues).nested ?? {});
 * };
 * ```
 */
export type ValidateFn<T = Record<string, unknown>> = (data: T) => FormErrors;

/**
 * When to validate fields:
 * - `change`: After first submit or blur on every keystroke (default)
 * - `blur`: After each field loses focus
 * - `submit`: Only on form submission
 */
export type ValidateOn = 'change' | 'blur' | 'submit';

/**
 * Result object passed to @onChange and @onSubmit callbacks.
 */
export interface FormResult<T extends Record<string, unknown> = Record<string, unknown>> {
  data: T;
  isValid: boolean;
  errors: FormErrors;
}

/** Union of values that text-based form fields can produce. */
export type FieldValue = string | number | undefined;

/**
 * Per-field helpers yielded via `form.field("name")`.
 */
export interface FieldApi {
  value: FieldValue;
  errors: string[];
  isTouched: boolean;
  onChange: (value: FieldValue) => void;
  onBlur: () => void;
}

/**
 * Form-level API yielded to the block.
 */
export interface FormApi<T extends Record<string, unknown> = Record<string, unknown>> {
  /** Get field-level helpers by name */
  field: (name: string) => FieldApi;
  /** Whether the form data passes validation */
  isValid: boolean;
  /** Whether any field has been modified */
  isDirty: boolean;
  /** Whether a submit is in progress */
  isSubmitting: boolean;
  /** All errors keyed by field name */
  errors: FormErrors;
  /** Current form data */
  data: T;
  /** Programmatically submit the form */
  submit: () => void;
  /** Reset to initial data */
  reset: () => void;
  /** Set a single field's value */
  setFieldValue: (name: string, value: FieldValue) => void;
  /** Mark a field as touched */
  setFieldTouched: (name: string) => void;
}

// ---------------------------------------------------------------------------
// Signature
// ---------------------------------------------------------------------------

interface FormSignature<T extends Record<string, unknown> = Record<string, unknown>> {
  Args: {
    /** Form data object — the source of truth */
    data: T;
    /**
     * Validation function. Given current data, returns errors keyed by field
     * name. Return `{}` when valid. Works with any validation library.
     *
     * @example Valibot
     * ```ts
     * const validate = (data) => {
     *   const r = v.safeParse(schema, data);
     *   return r.success ? {} : (v.flatten(r.issues).nested ?? {});
     * };
     * ```
     */
    validate?: ValidateFn<T>;
    /** Called on every field change */
    onChange?: (result: FormResult<T>) => void;
    /** Called on valid form submission (can return a promise) */
    onSubmit?: (result: FormResult<T>, event?: SubmitEvent) => void | Promise<void>;
    /** Called when form is submitted but invalid */
    onInvalidSubmit?: (result: FormResult<T>, event?: SubmitEvent) => void;
    /** Server-side errors to merge */
    serverErrors?: FormErrors;
    /** When to show validation errors @default "change" */
    validateOn?: ValidateOn;
  };
  Blocks: {
    default: [FormApi<T>];
  };
  Element: HTMLFormElement;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reimagined Form — an optional orchestrator that wraps form inputs
 * and provides validation, touched tracking, and error distribution.
 *
 * **Validation-library agnostic:** pass a `@validate` function that
 * returns `{ fieldName: ['error messages'] }`. Works out of the box
 * with Valibot, Zod, or hand-rolled checks.
 *
 * Every reimagined input (Input, Textarea, Checkbox, etc.) works
 * standalone. The Form component adds:
 * - Validation via `@validate` callback
 * - Automatic touched tracking per field
 * - Error distribution via `form.field("name").errors`
 * - Submit prevention when invalid
 * - `@onChange` / `@onSubmit` callbacks with typed data
 *
 * @example Form with Valibot validation
 * ```gts
 * import Form from 'tio-reimagined/components/form';
 * import Input from 'tio-reimagined/components/input';
 * import Button from 'tio-reimagined/components/button';
 * import * as v from 'valibot';
 *
 * const schema = v.object({
 *   name: v.pipe(v.string(), v.nonEmpty('Name is required')),
 *   email: v.pipe(v.string(), v.email('Invalid email')),
 * });
 *
 * const validate = (data) => {
 *   const r = v.safeParse(schema, data);
 *   return r.success ? {} : (v.flatten(r.issues).nested ?? {});
 * };
 *
 * <Form @data={{this.formData}} @validate={{validate}} @onSubmit={{this.save}} as |form|>
 *   {{#let (form.field "name") as |f|}}
 *     <Input @label="Name" @value={{f.value}} @errors={{f.errors}}
 *            @onChange={{f.onChange}} @onBlur={{f.onBlur}} @isRequired={{true}} />
 *   {{/let}}
 *   {{#let (form.field "email") as |f|}}
 *     <Input @label="Email" @type="email" @value={{f.value}} @errors={{f.errors}}
 *            @onChange={{f.onChange}} @onBlur={{f.onBlur}} @isRequired={{true}} />
 *   {{/let}}
 *   <Button type="submit" @intent="primary">Save</Button>
 * </Form>
 * ```
 */
export default class Form<
  T extends Record<string, unknown> = Record<string, unknown>,
> extends Component<FormSignature<T>> {
  // ---- Internal tracked state -----------------------------------------------

  @tracked private _touched: Touched = {};
  @tracked private _hasSubmitted = false;
  @tracked private _isSubmitting = false;
  @tracked private _fieldChangeCount = 0;

  /** Snapshot of initial data for dirty checking and reset */
  private _initialDataJSON: string;

  constructor(owner: Owner, args: FormSignature<T>['Args']) {
    super(owner, args);
    this._initialDataJSON = JSON.stringify(args.data);
  }

  // ---- Validation -----------------------------------------------------------

  private get validateOn(): ValidateOn {
    return this.args.validateOn ?? 'change';
  }

  /**
   * Run the consumer-provided validate function against current data.
   * Memoized with @cached so multiple template accesses in a single render
   * only invoke the validate function once.
   */
  @cached
  private get validationErrors(): FormErrors {
    if (!this.args.validate) return {};
    return this.args.validate(this.args.data);
  }

  /**
   * Merge client validation errors with server-side errors.
   */
  private get allErrors(): FormErrors {
    const clientErrors = this.validationErrors;
    const serverErrors = this.args.serverErrors ?? {};

    const merged: FormErrors = { ...clientErrors };
    for (const [key, msgs] of Object.entries(serverErrors)) {
      if (msgs && msgs.length > 0) {
        const existing = merged[key];
        merged[key] = existing ? [...existing, ...msgs] : [...msgs];
      }
    }
    return merged;
  }

  private get isValid(): boolean {
    return Object.keys(this.allErrors).length === 0;
  }

  private get isDirty(): boolean {
    // Consume _fieldChangeCount to establish a tracking dependency.
    // Without this, Glimmer's fine-grained rendering won't re-evaluate
    // isDirty after setFieldValue mutates data via bracket notation.
    void this._fieldChangeCount;
    return JSON.stringify(this.args.data) !== this._initialDataJSON;
  }

  // ---- Field helpers --------------------------------------------------------

  /**
   * Get errors for a specific field, respecting validation timing.
   */
  private errorsForField(name: string): string[] {
    const fieldErrors = this.allErrors[name];
    if (!fieldErrors) return [];

    switch (this.validateOn) {
      case 'submit':
        return this._hasSubmitted ? [...fieldErrors] : [];
      case 'blur':
        return this._touched[name] ? [...fieldErrors] : [];
      case 'change':
      default:
        return this._hasSubmitted || this._touched[name] ? [...fieldErrors] : [];
    }
  }

  /**
   * Returns a FieldApi object for a given field name.
   * Used in the template as `{{#let (form.field "email") as |f|}}`.
   */
  fieldHelper = (name: string): FieldApi => {
    const data = this.args.data;
    return {
      value: data[name] as FieldValue,
      errors: this.errorsForField(name),
      isTouched: Boolean(this._touched[name]),
      onChange: (value: FieldValue) => {
        this.setFieldValue(name, value);
      },
      onBlur: () => {
        this.setFieldTouched(name);
      },
    };
  };

  // ---- Actions --------------------------------------------------------------

  setFieldValue = (name: string, value: FieldValue) => {
    (this.args.data as Record<string, unknown>)[name] = value;
    this._fieldChangeCount++;

    if (this.validateOn === 'change') {
      this._touched = { ...this._touched, [name]: true };
    }

    this.args.onChange?.({
      data: this.args.data,
      isValid: this.isValid,
      errors: this.allErrors,
    });
  };

  setFieldTouched = (name: string) => {
    this._touched = { ...this._touched, [name]: true };
  };

  handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    this._hasSubmitted = true;

    if (this.isValid) {
      this._isSubmitting = true;
      try {
        await this.args.onSubmit?.({ data: this.args.data, isValid: true, errors: {} }, event);
      } finally {
        this._isSubmitting = false;
      }
    } else {
      this.args.onInvalidSubmit?.(
        { data: this.args.data, isValid: false, errors: this.allErrors },
        event
      );
    }
  };

  programmaticSubmit = async () => {
    this._hasSubmitted = true;

    if (this.isValid) {
      this._isSubmitting = true;
      try {
        await this.args.onSubmit?.({
          data: this.args.data,
          isValid: true,
          errors: {},
        });
      } finally {
        this._isSubmitting = false;
      }
    } else {
      this.args.onInvalidSubmit?.({
        data: this.args.data,
        isValid: false,
        errors: this.allErrors,
      });
    }
  };

  resetForm = () => {
    this._touched = {};
    this._hasSubmitted = false;
    this._isSubmitting = false;
    this._fieldChangeCount++;
    const initial = JSON.parse(this._initialDataJSON) as Record<string, unknown>;
    const data = this.args.data as Record<string, unknown>;
    for (const key of Object.keys(data)) {
      data[key] = initial[key];
    }
  };

  // ---- Template -------------------------------------------------------------

  <template>
    <form
      data-test-form
      novalidate
      class="flex flex-col gap-4"
      {{on "submit" this.handleSubmit}}
      ...attributes
    >
      {{yield
        (hash
          field=this.fieldHelper
          isValid=this.isValid
          isDirty=this.isDirty
          isSubmitting=this._isSubmitting
          errors=this.allErrors
          data=@data
          submit=this.programmaticSubmit
          reset=this.resetForm
          setFieldValue=this.setFieldValue
          setFieldTouched=this.setFieldTouched
        )
      }}
    </form>
  </template>
}

export type { FormSignature };
